// Types
import type { ModifyPlaylistAction } from '@jet-app/music-classical/action-model/ModifyPlaylistAction';
import type { Jet } from '~/jet';
import type { LoggerFactory } from '@amp/web-apps-logger';

// Utils for Intents
import { makeModifyPlaylistIntent } from '@jet-app/music-classical/intents/context-menu/playlists/ModifyPlaylistIntent';
import { makeInvalidationAction } from '@jet-app/music-classical/action-model/InvalidationAction';
import { pushNewPlaylist, updatePlaylistLabel } from '~/stores/playlists';

export type Dependencies = {
    jet: Jet;
    logger: LoggerFactory;
};

export function registerHandler(dependencies: Dependencies) {
    const { jet, logger } = dependencies;

    const log = logger.loggerFor('jet/action-handlers/modify-playlist-action');

    jet.onAction(
        'modifyPlaylistAction',
        async (action: ModifyPlaylistAction) => {
            log.info('received ModifyPlaylistAction:', action);

            try {
                const apiAction = makeModifyPlaylistIntent({
                    ...action,
                    actionType: action.type,
                });

                const response = await jet.dispatch(apiAction);

                // If the API returns type: confirmation, then use the segue to redirect to newly created playlist
                if (response.type === 'confirmation' && response?.segue) {
                    jet.perform(response?.segue);

                    // Also add new playlist to the store so that it shows up on sidebar
                    pushNewPlaylist({
                        label: response?.title,
                        id: response?.segue?.destination?.id,
                        url: response?.segue?.contentDescriptor?.url,
                        segue: response?.segue,
                        canEdit: true,
                        canDelete: true,
                    });
                }

                if (
                    response.type === 'done' &&
                    action.type === 'editPlaylist'
                ) {
                    jet.perform(
                        makeInvalidationAction({
                            kind: 'libraryPlaylistUpdated',
                        }),
                    );

                    updatePlaylistLabel(action.playlistId, action.name);
                }

                return 'performed';
            } catch (e) {
                log.error('Modify Playlist Action Error:', e);
                throw new Error(
                    'an error occurred while handling Modify Playlist Action',
                );
            }
        },
    );
}
